<template>
  <div class="facebook-prefill">
    <button
      :class="buttonClass"
      :disabled="completed"
      type="button"
      @click.prevent="loginToFacebook"
    >
      <icon
        v-if="loading"
        key="spinner"
        class="spinner"
        icon-name="loader-fa"
        view-box="0 0 28 28"
      />
      <template v-else>
        <icon
          key="facebook"
          icon-name="facebook-fa"
          view-box="0 0 24 28"
        />
        <span>{{ buttonText }}</span>
      </template>
    </button>

    <p
      v-if="error"
      role="alert"
      class="facebook-prefill__alert p2a-error-message"
    >{{ error }}</p>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Icon from "StyleguideComponents/Icon.vue";
import StringFilters from "JS/filters/stringFilters";
import FacebookService from "JS/services/campaign/FacebookService";

export default {
  name: "FacebookPrefill",
  components: {
    Icon
  },
  props: {
    appId: {
      type: String,
      required: true
    },
    buttonText: {
      type: String,
      default: "Sign with Facebook"
    },
    language: {
      type: String,
      default: "EN"
    },
    socialSignIn: {
      type: Object,
      default: () => {}
    },
    version: {
      type: String,
      default: "v2.5"
    }
  },
  data() {
    return {
      completed: false,
      error: "",
      loading: false
    };
  },
  computed: {
    buttonClass() {
      return [
        "btn btn--lg btn--facebook",
        {
          "btn--pressed": this.completed
        }
      ];
    }
  },
  mounted() {
    FacebookService.loadFacebookJs(this.language);
    FacebookService.initializeFacebook(this.appId, this.version);
  },
  methods: {
    ...mapActions({
      setFormValue: "forms/formValues"
    }),
    checkLoginStatus(loginState) {
      return (
        loginState.authResponse &&
        loginState.status === FacebookService.STATUS_CONNECTED
      );
    },
    async checkPermissions(loginState) {
      if (!this.checkLoginStatus(loginState)) {
        return false;
      }

      try {
        const declinedPermissions = await FacebookService.getDeclinedPermissions(
          loginState.authResponse.userID
        );

        if (declinedPermissions.data && declinedPermissions.data.length < 1) {
          return true;
        }
      } catch (error) {
        // unable to retrieve permissions, user may have logged out
        this.error = "Unable to authorize with Facebook. Please try again.";
        return false;
      }

      return false;
    },
    async loginToFacebook() {
      this.error = "";
      this.loading = true;
      this.completed = false;

      const loginState = await FacebookService.getLoginState();
      const hasPermissions = await this.checkPermissions(loginState);

      if (!hasPermissions) {
        try {
          await FacebookService.promptForLogin();
        } catch (error) {
          // login failed, reset button
          this.error = "Unable to authorize with Facebook. Please try again.";
          this.loading = false;
          return false;
        }
      }

      this.loading = false;
      this.handleLogin();
      return true;
    },
    fillFormValues(user) {
      /* eslint-disable camelcase */
      const { email, first_name, last_name, gender } = user;
      const fields = new Map([
        ["email", email],
        ["fullName", StringFilters.fullName(first_name, last_name)],
        ["title", StringFilters.prefix(gender)]
      ]);

      fields.forEach((value, fieldName) => {
        if (value) {
          this.setFormValue({ fieldName, value });
        }
      });
    },
    async handleLogin() {
      try {
        const user = await FacebookService.getUserInfo();

        this.fillFormValues(user);
        this.completed = true;
        this.error = "";
      } catch (error) {
        // unable to get user details
        this.error = "An unexpected error occurred. Please try again later.";
      }
    }
  }
};
</script>
